import Tooltip from '@/components/Tooltip';
import {
  CampaignHistoryQuickButton,
  CampaignHistoryButton,
  type CampaignHistoryItem,
  type CampaignHistoryChannelInfo,
} from '@/components/CampaignHistoryPopover';
import { useNameColumnButtons } from '@/lib/utils/nameColumnButtons';
import { useI18nText } from '@/lib/utils/i18nText';
import { useChannelEditDialog } from '@/lib/hooks/useChannelEditDialog';
import { useAppSelector } from '@/store/hooks';
import { usePreviewMode } from '@/contexts/PreviewModeContext';

/**
 * Single source of truth for each channel quick-button's icon + bilingual tooltip. Defined
 * once here and consumed by both this component and the influencer dashboard's `InfluencerRow`
 * (which keeps its own anchor/handler wiring) so the labels never drift apart.
 */
export const CHANNEL_QUICK_BUTTON_META = {
  chat:    { icon: 'fa-comments',   tooltipEn: '"Chat System" as a drawer',     tooltipKo: '"채팅 시스템" 드로어패널로 보기' },
  details: { icon: 'fa-user',       tooltipEn: '"Channel Details" as a drawer', tooltipKo: '"채널 상세" 드로어패널로 보기' },
  stats:   { icon: 'fa-line-chart', tooltipEn: '"Open stats" as a drawer',      tooltipKo: '"통계 보기" 드로어패널로 보기' },
  edit:    { icon: 'fa-pencil',     tooltipEn: '"Edit Channel" as a popup',     tooltipKo: '"채널 수정" 팝업으로 보기' },
  history: { icon: 'fa-history',    tooltipEn: '"Campaign History" as a popup',  tooltipKo: '"캠페인 이력" 팝업으로 보기' },
} as const;

/**
 * Quick-action buttons shown under the channel name for whichever popover actions the admin
 * has checked (global `useNameColumnButtons` preference — the same store `ChannelLinksPopover`'s
 * checkboxes toggle). Shared by every page with a channel-name column so the row of buttons is
 * defined once.
 *
 * - Chat / Details / Stats open the channel's right drawer, so the host page injects the handlers
 *   from `useChannelDrawer()` — `onChat`→`openChatDrawer`, `onDetails`→`openPanelDrawer`,
 *   `onStats`→`openStatsDrawer`. The shared tooltip (CHANNEL_QUICK_BUTTON_META) promises each opens
 *   "as a drawer", so do NOT wire these to `window.open`/a new tab — that silently contradicts the
 *   tooltip (past bug). Omitting a handler simply no-ops its button.
 * - Edit (loads the user + opens the shared `EditChannelDialog`) and History (campaign-history
 *   popover) are self-contained and identical everywhere, so they live here.
 *
 * All buttons render at one compact size — the segmented group's `.actions-btn` rules control
 * the metrics — so no size prop is needed.
 */
export function ChannelNameQuickButtons({
  userId,
  onChat,
  onDetails,
  onStats,
  campaignHistory,
}: {
  userId: string;
  onChat?: (userId: string) => void;
  onDetails?: (userId: string) => void;
  onStats?: (userId: string) => void;
  /** Provide when the host page already has this channel's campaign enrollments + the
   *  campaign catalog loaded (via `buildCampaignHistoryItems`) so the History button shows
   *  its count badge immediately — like the influencer dashboard — instead of falling back
   *  to `CampaignHistoryQuickButton`'s on-demand self-fetch, which can't know the count
   *  before the popover is opened. */
  campaignHistory?: { items: CampaignHistoryItem[]; channel?: CampaignHistoryChannelInfo };
}) {
  const nameColButtons = useNameColumnButtons();
  const { t } = useI18nText();
  const isPreview = usePreviewMode();
  const role = useAppSelector((s) => s.envs.currentUser?.role);
  const channelEdit = useChannelEditDialog();

  // Every quick button here (Chat / Details / Stats / Edit / History) is an
  // internal admin action, so hide the whole row for advertiser-preview and
  // guest-token sessions — only a real admin outside preview mode sees them
  // (mirrors ChannelLinksPopover's `showInternalActions` gate). Without this
  // the default-on Chat button leaks into the guest advertiser link page.
  if (!(role === 'admin' && !isPreview)) return null;

  const btnClass = 'actions-btn';
  const buttons: React.ReactNode[] = [];

  // Chat / Details / Stats share one button shape — only the injected handler differs. Icon +
  // tooltip come from the shared CHANNEL_QUICK_BUTTON_META so they never drift from InfluencerRow.
  const navButton = (key: 'chat' | 'details' | 'stats', onClick?: (userId: string) => void) => {
    const meta = CHANNEL_QUICK_BUTTON_META[key];
    return (
      <Tooltip key={key} content={t(meta.tooltipEn, meta.tooltipKo)} side="bottom">
        <button type="button" className={`${btnClass} actions-btn--${key}`} onClick={() => onClick?.(userId)}>
          <i className={`fa ${meta.icon}`} />
        </button>
      </Tooltip>
    );
  };

  if (nameColButtons.chat) buttons.push(navButton('chat', onChat));
  if (nameColButtons.details) buttons.push(navButton('details', onDetails));
  // Stats before Edit — matches InfluencerRow's own quick-button order (and the
  // ChannelLinksPopover checkbox list above it), so the row reads the same
  // regardless of which page renders it.
  if (nameColButtons.stats) buttons.push(navButton('stats', onStats));
  if (nameColButtons.edit) {
    const meta = CHANNEL_QUICK_BUTTON_META.edit;
    buttons.push(
      <Tooltip key="edit" content={t(meta.tooltipEn, meta.tooltipKo)} side="bottom">
        <button
          type="button"
          className={`${btnClass} actions-btn--edit`}
          disabled={channelEdit.loading}
          onClick={() => void channelEdit.openFor(userId)}
        >
          <i className={`fa ${channelEdit.loading ? 'fa-spinner fa-spin' : meta.icon}`} />
        </button>
      </Tooltip>,
    );
  }
  if (nameColButtons.history) {
    const meta = CHANNEL_QUICK_BUTTON_META.history;
    buttons.push(
      campaignHistory ? (
        <CampaignHistoryButton
          key="history"
          items={campaignHistory.items}
          channel={campaignHistory.channel}
          size="md"
          tooltip={
            campaignHistory.items.length > 0
              ? t(meta.tooltipEn, meta.tooltipKo)
              : t('This channel has no participated campaign history.', '이 채널은 참여된 캠페인 이력이 없습니다.')
          }
          tooltipSide="bottom"
        />
      ) : (
        <CampaignHistoryQuickButton
          key="history"
          userId={userId}
          size="md"
          tooltip={t(meta.tooltipEn, meta.tooltipKo)}
          tooltipSide="bottom"
        />
      ),
    );
  }

  if (buttons.length === 0 && !channelEdit.dialog) return null;

  return (
    <>
      {buttons.length > 0 && (
        // The line wrapper forces the button group onto its own row below the channel name
        // (block-level / full flex line); the inner group box hugs the buttons.
        <div className="influencer-name-actions-line">
          <div className="influencer-name-actions">{buttons}</div>
        </div>
      )}
      {channelEdit.dialog}
    </>
  );
}
